import { useEffect, useState } from 'react';
import { getOnlineVisaCountriesApi } from '../api/onlineVisaApi';

/**
 * Shared hook for fetching the country list used in both the search form
 * (destination + nationality dropdowns) and the search-results page header.
 *
 * The hook caches the fetched list on `window` so multiple mounts in the same
 * session don't trigger additional network calls.
 */
const CACHE_KEY = '__online_visa_countries_cache__';

const normaliseCountries = (list) =>
  (Array.isArray(list) ? list : []).map((item) => {
    const countryCode = (
      item.countryCode ||
      item.country_code ||
      item.value ||
      ''
    ).toUpperCase();
    const countryName =
      item.countryName || item.country_name || item.label || '';
    return {
      ...item,
      countryCode,
      countryName,
      value: countryCode,
      label: countryName,
    };
  });

const useCountries = () => {
  const [countries, setCountries] = useState(
    typeof window !== 'undefined' && window[CACHE_KEY]
      ? normaliseCountries(window[CACHE_KEY])
      : [],
  );
  const [loading, setLoading] = useState(
    !(typeof window !== 'undefined' && window[CACHE_KEY]),
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // If we already have cached countries, skip the fetch.
    if (
      typeof window !== 'undefined' &&
      window[CACHE_KEY] &&
      window[CACHE_KEY].length > 0
    ) {
      const cachedCountries = normaliseCountries(window[CACHE_KEY]);
      window[CACHE_KEY] = cachedCountries;
      setCountries(cachedCountries);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      setLoading(true);
      setError(null);
      const data = await getOnlineVisaCountriesApi();
      if (cancelled) return;
      const nextCountries = normaliseCountries(data);
      if (typeof window !== 'undefined') {
        window[CACHE_KEY] = nextCountries;
      }
      setCountries(nextCountries);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Pre-mapped for react-select: { label, value, ...raw }
  const options = countries.map((c) => ({
    label: c.countryName,
    value: c.countryCode,
    ...c,
  }));

  return { countries, options, loading, error };
};

export default useCountries;
